import {
	AppstoreOutlined,
	MenuOutlined,
	PlusOutlined,
	ReloadOutlined,
} from "@ant-design/icons";
import {
	Alert,
	Button,
	Card,
	Col,
	message,
	Row,
	Space,
	Spin,
	Typography,
} from "antd";
import type React from "react";
import { useEffect, useState } from "react";
import type { RichMenuTabGroupWithRichmenus } from "../../../../shared/types/richmenu.types";
import { useRichMenuTabGroups } from "../../../richmenu-tab-group/hooks/useRichmenuTabGroupQueries";
import { useRichMenuTemplates } from "../../hooks/useRichmenuQueries";
import { RichMenuModal } from "./RichMenuModal";
import { RichMenuTabGroupsList } from "./RichMenuTabGroupsList";
import { RichMenuTemplatesPanel } from "./RichMenuTemplatesPanel";

const { Title, Paragraph } = Typography;

interface RichMenuManagementFormProps {
	/**
	 * API base URL
	 * @default '/api2'
	 */
	apiBaseUrl?: string;
	/**
	 * Upload URL for richmenu images
	 * @default '/assets/public/richmenus'
	 */
	uploadUrl?: string;
	/**
	 * Callback when richmenu is updated
	 */
	onUpdate?: (action: string, data: any) => void;
}

export const RichMenuManagementForm: React.FC<RichMenuManagementFormProps> = ({
	apiBaseUrl = "/api2",
	uploadUrl = "/assets/public/richmenus",
	onUpdate,
}) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedTabGroup, setSelectedTabGroup] =
		useState<RichMenuTabGroupWithRichmenus | null>(null);

	const {
		tabGroups,
		loading: tabGroupsLoading,
		error: tabGroupsError,
		refetch: refetchTabGroups,
	} = useRichMenuTabGroups();

	const {
		templates,
		loading: templatesLoading,
		error: templatesError,
		refetch: refetchTemplates,
	} = useRichMenuTemplates();

	// Handle errors
	useEffect(() => {
		if (tabGroupsError) {
			message.error(`タブグループの読み込みに失敗しました: ${tabGroupsError}`);
		}
		if (templatesError) {
			message.error(`テンプレートの読み込みに失敗しました: ${templatesError}`);
		}
	}, [tabGroupsError, templatesError]);

	const handleCreateNew = () => {
		setSelectedTabGroup(null);
		setIsModalOpen(true);
	};

	const handleEditTabGroup = (tabGroup: RichMenuTabGroupWithRichmenus) => {
		setSelectedTabGroup(tabGroup);
		setIsModalOpen(true);
	};

	const handleModalClose = () => {
		setIsModalOpen(false);
		setSelectedTabGroup(null);
		refetchTabGroups();
	};

	const _handleRefresh = () => {
		refetchTabGroups();
		refetchTemplates();
		message.success("データを更新しました");
	};

	const loading = tabGroupsLoading || templatesLoading;
	const error = tabGroupsError || templatesError;

	if (error) {
		return (
			<Alert
				message="リッチメニューデータの読み込みエラー"
				description={error?.message}
				type="error"
				action={
					<Button danger onClick={() => window.location.reload()}>
						<ReloadOutlined /> リロード
					</Button>
				}
			/>
		);
	}

	return (
		<Spin spinning={loading}>
			<div style={{ padding: "0 8px" }}>
				{/* Main Content */}
				<Row gutter={[16, 16]}>
					{/* Tab Groups List */}
					<Col xs={24} xl={16}>
						<Card
							title={
								<div
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										width: "100%",
									}}
								>
									<Space>
										<MenuOutlined style={{ color: "#1890ff" }} />
										<span>リッチメニュータブグループ</span>
									</Space>
									<Button
										type="primary"
										icon={<PlusOutlined />}
										onClick={handleCreateNew}
										style={{
											background:
												"linear-gradient(135deg, #52c41a 0%, #389e0d 100%)",
											borderColor: "#52c41a",
										}}
									>
										新しいリッチメニューグループを作成
									</Button>
								</div>
							}
							variant="outlined"
							styles={{
								header: {
									background:
										"linear-gradient(135deg, #e6f4ff 0%, #bae0ff 100%)",
									borderBottom: "1px solid #91caff",
								},
							}}
							style={{
								minHeight: "600px",
							}}
						>
							<RichMenuTabGroupsList
								tabGroups={tabGroups || []}
								onEdit={handleEditTabGroup}
								onUpdate={onUpdate}
								uploadUrl={uploadUrl}
								onCreateNew={handleCreateNew}
							/>
						</Card>
					</Col>

					{/* Templates Panel */}
					<Col xs={24} xl={8}>
						<Card
							title={
								<Space>
									<AppstoreOutlined style={{ color: "#722ed1" }} />
									<span>テンプレート</span>
								</Space>
							}
							variant="outlined"
							styles={{
								header: {
									background:
										"linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)",
									borderBottom: "1px solid #d3adf7",
								},
							}}
							style={{
								minHeight: "600px",
							}}
						>
							<RichMenuTemplatesPanel
								templates={templates}
								loading={templatesLoading}
							/>
						</Card>
					</Col>
				</Row>

				{/* Richmenu Modal */}
				<RichMenuModal
					visible={isModalOpen}
					onClose={handleModalClose}
					tabGroup={selectedTabGroup}
					templates={templates}
					uploadUrl={uploadUrl}
				/>
			</div>
		</Spin>
	);
};
